import type { OrderFeeResponseDto } from "Infrastructure/Api/Api";
import { UserPermissions } from "Infrastructure/Api/permissions";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { hasPermission } from "Infrastructure/Utilities/Permissions/hasPermission";
import { useUpdateOrderFeeMutation } from "Order/Modules/Payment/Api/Mutation/useUpdateFeeMutation";
import {
	type EditFeeFormModel,
	getEditFeeFormModelSchema,
} from "Order/Modules/Payment/Components/Forms/Schema/editFeeFormSchema";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Grid2 } from "@mui/material";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

type EditFeeFormProps = {
	fees: OrderFeeResponseDto;
	orderId: number;
	isDisabled?: boolean;
};

const paymentResource = Resources.base.payment.shared;

export function EditFeeForm({
	fees,
	orderId,
	isDisabled = false,
}: EditFeeFormProps) {
	const { t } = useResource();

	const formDefaultValues = {
		serviceFeePercentage: fees.serviceFeePercentage ?? 0,
		serviceFeeDiscountPercentage: fees.serviceFeeDiscountPercentage ?? 0,
		customerVATPercentage: fees.customerVATPercentage ?? 0,
		depositPercentage: fees.depositPercentage ?? 0,
	};

	const form = useForm<EditFeeFormModel>({
		resolver: zodResolver(getEditFeeFormModelSchema()),
		defaultValues: formDefaultValues,
	});

	const {
		control,
		formState: { errors },
	} = form;

	const { mutate: updateFee, isPending } = useUpdateOrderFeeMutation(
		orderId,
		fees.id,
	);

	useEffect(() => {
		form.reset(formDefaultValues);
	}, [fees]);

	const { handleServerError } = useHandleServerError(form);

	const handleSubmitUpdate = () => {
		handleServerError(updateFee);
	};

	const hasUpdateFeePermission = hasPermission(
		UserPermissions.OrderFeeModule.update,
	);

	return (
		<>
			<Grid2 container spacing={{ xs: 1, sm: 2 }}>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Input
						id="serviceFeePercentage"
						name="serviceFeePercentage"
						label={t(paymentResource.serviceFeePercentage)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						isNumber
						isFloat
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Input
						id="amount"
						name="serviceFeeDiscountPercentage"
						label={t(paymentResource.serviceFeeDiscountPercentage)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						isNumber
						isFloat
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Input
						id="amount"
						name="customerVATPercentage"
						label={t(paymentResource.customerVATPercentage)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						isNumber
						isFloat
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Input
						id="amount"
						name="depositPercentage"
						label={t(paymentResource.depositPercentage)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						isNumber
						isFloat
					/>
				</Grid2>
			</Grid2>
			<Box>
				<Button
					variant="contained"
					onClick={handleSubmitUpdate}
					loading={isPending}
					sx={{ mt: 2 }}
					disabled={!hasUpdateFeePermission || isDisabled}
				>
					{t(paymentResource.save)}
				</Button>
			</Box>
		</>
	);
}
